import * as React from "react";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Box, Button, Dialog, DialogContent, DialogTitle, IconButton, Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography } from "@mui/material";
import { DataGrid, GridColDef, GridSortModel } from "@mui/x-data-grid";
import axiosClient from "../../../../../utils/api/axiosClient";
import { defaultPageModel, pageSizeOptions } from "../../../../../utils/constant";
import { getToken } from "../../../../../utils/functions";
import { showToast } from "../../../../../utils/toast";

type ReportedMemberRow = {
  id: number;
  userId: number;
  memberId?: string;
  name?: string;
  gender?: string;
  marriedCount: number;
  fakeCount: number;
  nonResponsiveCount: number;
  totalReports: number;
};

type ReportedByMemberRow = {
  userId: number;
  memberId?: string;
  name?: string;
  mobile?: string;
};

const reasonLabelMap: Record<string, string> = {
  MARRIED: "Married",
  FAKE: "Fake",
  NON_RESPONSIVE: "None Responsive",
};

export default function ReportedMember() {
  const [pageModel, setPageModel] = React.useState(defaultPageModel);
  const [rows, setRows] = React.useState<ReportedMemberRow[]>([]);
  const [rowCount, setRowCount] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [sortModel, setSortModel] = React.useState<GridSortModel>([]);
  const [detailsOpen, setDetailsOpen] = React.useState(false);
  const [detailsLoading, setDetailsLoading] = React.useState(false);
  const [detailsRows, setDetailsRows] = React.useState<ReportedByMemberRow[]>([]);
  const [detailsTitle, setDetailsTitle] = React.useState("");

  const fetchRows = React.useCallback(() => {
    setLoading(true);
    const activeSort = sortModel[0];
    const params: Record<string, string> = {};
    if (search.trim()) params.search = search.trim();
    if (activeSort?.field) params.sortField = activeSort.field;
    if (activeSort?.sort) params.sortDirection = activeSort.sort;
    axiosClient
      .get(`/v1/interests/reported-member-summaries/${pageModel.page + 1}/${pageModel.pageSize}`, {
        headers: { Authorization: getToken() },
        params,
        skipGlobalLoader: true,
      })
      .then((res) => {
        const { content, totalElements } = res.data;
        setRows(content || []);
        setRowCount(totalElements || 0);
      })
      .finally(() => setLoading(false));
  }, [pageModel, search, sortModel]);

  React.useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const handleDeleteReports = React.useCallback((userId: number) => {
    if (!window.confirm("Are you sure you want to delete all reports for this profile?")) return;

    axiosClient
      .post(`/v1/interests/delete-reports/${userId}`, {}, { headers: { Authorization: getToken() } })
      .then(() => {
        showToast("Reports deleted successfully", "success");
        fetchRows();
      })
      .catch((error) => {
        showToast(error?.response?.data?.message || error?.message || "Failed to delete reports", "error");
      });
  }, [fetchRows]);

  const handleOpenDetails = React.useCallback((row: ReportedMemberRow, reason: "MARRIED" | "FAKE" | "NON_RESPONSIVE") => {
    setDetailsTitle(`${reasonLabelMap[reason]} Reported Members`);
    setDetailsRows([]);
    setDetailsOpen(true);
    setDetailsLoading(true);
    axiosClient
      .get(`/v1/interests/reported-by-members/${row.userId}/${reason}`, {
        headers: { Authorization: getToken() },
        skipGlobalLoader: true,
      })
      .then((res) => {
        setDetailsRows(res?.data?.data || []);
      })
      .finally(() => setDetailsLoading(false));
  }, []);

  const columns = React.useMemo(
    () =>
      [
        {
          field: "memberId",
          headerName: "Profile ID",
          minWidth: 150,
          flex: 0.9,
          renderCell: (params: any) => params.row.memberId || params.row.userId,
        },
        {
          field: "name",
          headerName: "Name",
          minWidth: 160,
          flex: 1.1,
          renderCell: (params: any) => params.row.name || "-",
        },
        {
          field: "gender",
          headerName: "Gender",
          width: 95,
          renderCell: (params: any) => params.row.gender || "-",
        },
        {
          field: "marriedCount",
          headerName: "Married",
          width: 90,
          renderCell: (params: any) => (
            <Button
              variant="text"
              size="small"
              disabled={!params.row.marriedCount}
              onClick={() => handleOpenDetails(params.row, "MARRIED")}
            >
              {params.row.marriedCount}
            </Button>
          ),
        },
        {
          field: "fakeCount",
          headerName: "Fake",
          width: 80,
          renderCell: (params: any) => (
            <Button
              variant="text"
              size="small"
              disabled={!params.row.fakeCount}
              onClick={() => handleOpenDetails(params.row, "FAKE")}
            >
              {params.row.fakeCount}
            </Button>
          ),
        },
        {
          field: "nonResponsiveCount",
          headerName: "None Responsive",
          width: 140,
          renderCell: (params: any) => (
            <Button
              variant="text"
              size="small"
              disabled={!params.row.nonResponsiveCount}
              onClick={() => handleOpenDetails(params.row, "NON_RESPONSIVE")}
            >
              {params.row.nonResponsiveCount}
            </Button>
          ),
        },
        {
          field: "action",
          headerName: "Delete",
          width: 90,
          sortable: false,
          filterable: false,
          renderCell: (params: any) => (
            <Tooltip title="Delete report">
              <IconButton color="error" size="small" onClick={() => handleDeleteReports(params.row.userId)}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ),
        },
      ] as GridColDef[],
    [handleDeleteReports, handleOpenDetails]
  );

  return (
    <div className="app-content pt-3 p-md-3 p-lg-4">
      <Paper sx={{ height: "80vh", display: "flex", flexDirection: "column" }}>
        <Box sx={{ p: 2, display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
          <Typography variant="h6">Reported Members</Typography>
          <TextField
            size="small"
            label="Search"
            placeholder="Profile ID, Name, Phone"
            value={search}
            onChange={(event) => {
              setPageModel((prev) => ({ ...prev, page: 0 }));
              setSearch(event.target.value);
            }}
            sx={{ minWidth: 280 }}
          />
        </Box>
        <DataGrid
          sx={{ flex: 1, minHeight: 0 }}
          rows={rows}
          columns={columns}
          loading={loading}
          pagination
          paginationMode="server"
          sortingMode="server"
          paginationModel={pageModel}
          onPaginationModelChange={setPageModel}
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          rowCount={rowCount}
          pageSizeOptions={pageSizeOptions}
          disableRowSelectionOnClick
        />
      </Paper>
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{detailsTitle}</DialogTitle>
        <DialogContent>
          {detailsLoading ? (
            <Typography>Loading...</Typography>
          ) : !detailsRows.length ? (
            <Typography>No members found.</Typography>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Profile ID</TableCell>
                  <TableCell>Mobile Number</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {detailsRows.map((item) => (
                  <TableRow key={item.userId}>
                    <TableCell>{item.name || "-"}</TableCell>
                    <TableCell>{item.memberId || item.userId}</TableCell>
                    <TableCell>{item.mobile || "-"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
